import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import { CoopSwiperBreakpointsUnique } from '../../../../../utility/coopSwiperBreakpointsUnique';
import type { A11yTextType } from '../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import { mapVariantPriceData } from '../../../../common/models/domain/ecommerce/promotion/promotionMappers';
import type { PriceType } from '../../../../common/models/priceData/priceData';
import type { ProductVariantProps } from '../../../../common/models/props/itemTeaserProps';
import {
    getProductAttributesA11yText,
    getProductCustomerInformationText,
    getProductPackageSizeInfoText,
} from '../../../../ecommerce/components/ProductInfoProperty';
import type { RecipeItemState } from '../../../../recipe/models';
import { mapToProductListingItemData } from '../ProductListingItem';
import type { RecipeProductListingItemData } from './RecipeProductListingItem.types';
import { getRecipeProductListingItemPriceA11yText } from './RecipeProductListingItemPriceLabels';

export const mapToRecipeProductListingItemData = (
    recipeItem: RecipeItemState,
    variant: ProductVariantProps | undefined,
    product?: ApiProduct,
): RecipeProductListingItemData => {
    const productListingItemData = product && mapToProductListingItemData(product);
    const maybeVariantPriceData =
        product &&
        mapVariantPriceData(
            {
                priceData: product.priceData,
                promotionPriceData: product.promotionPriceData,
                promotion: product.promo,
            },
            variant,
        );

    return {
        identifier: product?.identifier || recipeItem.id,
        name: productListingItemData?.name || recipeItem.name,
        manufacturer: productListingItemData?.manufacturer || recipeItem.manufacturer || '',
        consumerInformationText: productListingItemData?.consumerInformationText || [],
        imageUrl: productListingItemData?.imageUrl || recipeItem.image,
        packageSize: variant ? undefined : productListingItemData?.packageSize,
        packageSizeInformation:
            variant?.name || // variant is not chosen by user, so always display variant name as package size if available
            productListingItemData?.packageSizeInformation ||
            recipeItem.packageSizeInformation,
        url: productListingItemData?.url || '',
        attributeLabels: productListingItemData?.attributeLabels || [],

        priceData: maybeVariantPriceData?.priceData,
        promotionPriceData: maybeVariantPriceData?.promotionPriceData,
        promotion: maybeVariantPriceData?.promotion,
        recycleFeeData: product?.details.recycleFeeData,
        historicalPriceData: product?.historicalPriceData,
        comparativePrice: product?.comparativePrice,
    };
};

export const getRecipeProductListingItemA11yText = (params: {
    product: RecipeProductListingItemData;
    recipeItem: RecipeItemState;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    purchasable: boolean;
    isB2B: boolean;
    isUserMember: boolean;
    arePILPricesEnabled: boolean;
    isPILPricesLogicEnabled: boolean;
}) => {
    const priceTexts = params.purchasable
        ? getRecipeProductListingItemPriceA11yText({
              product: params.product,
              recipeItem: params.recipeItem,
              priceType: params.priceType,
              a11yTextType: params.a11yTextType,
              isB2B: params.isB2B,
              isUserMember: params.isUserMember,
              arePILPricesEnabled: params.arePILPricesEnabled,
              isPILPricesLogicEnabled: params.isPILPricesLogicEnabled,
          })
        : undefined;

    const a11yText = [
        params.product.name,
        params.product.manufacturer,
        getProductPackageSizeInfoText(params.product)?.a11y,
        getProductCustomerInformationText(params.product)?.a11y,
        priceTexts?.primaryText,
        priceTexts?.secondaryText,
        getProductAttributesA11yText(params.product),
        priceTexts?.totalText,
    ]
        .filter(Boolean)
        .join(', ');

    return a11yText;
};

export const recipeModalProductSubstitutesBreakpoints =
    CoopSwiperBreakpointsUnique.getUniqueBreakpointsFor(
        CoopSwiperBreakpointsUnique.AREA_RECIPE_MODAL,
    ).ProductList;
